/**
 * Hopper Voice — Unified microcopy system for the entire product.
 * All user-facing text should reference these constants.
 * Hopper is calm, observant, encouraging, and knowledgeable.
 */

export const HOPPER_VOICE = {
  // ── Loading States ──
  loading: {
    analyzingYard: "Looking at your yard…",
    matchingPlants: "Checking what grows well here…",
    buildingPlan: "Building your personalized plan…",
    checkingLocation: "Looking at your location…",
    filteringSoil: "Matching plants to your soil and sunlight…",
    selectingSpecies: "Selecting the right native species…",
    designingLayout: "Putting your plan together…",
    generatingPdf: "Preparing your plan document…",
    default: "Getting things ready…",
  },

  // ── Empty States ──
  empty: {
    noData: "Tell me about your yard and I'll help you get started.",
    noPlantsFound: "Nothing matched yet — try adjusting your filters and I'll find better options.",
    noPlans: "You haven't created a plan yet. Let's get started.",
    noGardens: "No gardens here yet — yours could be the first.",
  },

  // ── Success States ──
  success: {
    planReady: "Your plan is ready — here's what will thrive in your yard.",
    planSaved: "Your plan has been saved.",
    planDeleted: "Plan removed.",
    emailSaved: "You're in! Watch for seasonal tips.",
  },

  // ── Error States ──
  error: {
    generic: "Something didn't load correctly — try again and I'll take another look.",
    planLoad: "Couldn't load your plans right now. Please try again.",
    pdfFailed: "Couldn't prepare your plan document. Please try again.",
    networkError: "Having trouble connecting — check your connection and try again.",
    subscriptionError: "Couldn't update your plan access. Please try again.",
  },

  // ── Locked / Gated Content ──
  locked: {
    headline: "This is where your full plan comes together.",
    preview: "You're seeing a preview — unlock your full plan to get the complete layout and plant list.",
    nudge: "You're close — this is the difference between guessing and having a clear plan.",
    wildlife: "See exactly which wildlife your garden supports.",
    pdf: "Unlock your plan to download the full document.",
    photoOverlay: "See your garden plan overlaid on your actual yard photo.",
  },

  // ── CTAs / Button Labels ──
  cta: {
    createPlan: "Create My Plan",
    buildPlan: "Build My Plan",
    startPlan: "Start My Garden Plan",
    unlockFull: "Unlock My Full Plan",
    seeWhatWorks: "See What Works in My Yard",
    refinePlan: "Refine My Plan",
    explorePlants: "Explore More Plants",
    viewPlans: "See Plan Options",
    downloadPdf: "Download My Plan",
    keepExploring: "Keep exploring",
    tryAgain: "Try Again",
  },

  // ── Upgrade Moments ──
  upgrade: {
    paywallHeadline: "Your Yard Plan Is Ready",
    paywallSubtext: "Here's a preview — unlock your full plan to see everything.",
    modalTitle: "Unlock Your Full Plan",
    modalDescription: "Your preview shows a small sample. Your full plan includes everything tailored to your yard.",
    trustLine: "Most people don't fail at gardening because of effort — they plant the wrong combination.",
    beliefShift: "No guesswork. Just plants that actually work in your yard.",
  },

  // ── Dashboard / Return ──
  dashboard: {
    welcome: "Here's what's happening in your yard right now",
    seasonalNudge: (action: string) => `Based on your plan, this is a great time to ${action}.`,
    monthlyGuideTitle: (month: string) => `Hopper's Guide for ${month}`,
    monthlyGuideIntro: "Here's what your garden needs this month — based on your plan and your region.",
  },

  // ── Hero / Homepage ──
  hero: {
    headline: "Stop Guessing What Will Grow in Your Yard",
    subtext: "Get a personalized native garden plan based on your soil, sun, and location — in under 60 seconds.",
    trustLine: "No guesswork. Just plants that actually work in your yard.",
  },

  // ── Contextual Guidance ──
  guidance: {
    whyThisPlant: "This would work well in your yard because",
    whatToExpect: "Here's what to expect next",
    goodTime: "This is a good time to",
    planExplainer: "These recommendations are based on your yard's ecological conditions — not generic templates.",
  },
} as const;
